import { ArrowLeft } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

export default function TermsAndConditions() {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-dark-50">
      {/* Header */}
      <header className="bg-dark-100 border-b border-dark-200">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-vapour-600 hover:text-vapour-900 mr-3"
            aria-label="Go back"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-xl font-semibold text-vapour-900">Terms and Conditions</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-dark-200 p-8">
          
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-vapour-900 mb-4">Terms and Conditions</h1>
            <p className="text-vapour-600 text-lg">
              Effective Date: September 4, 2025 | Last Updated: September 4, 2025
            </p>
          </div>

          <div className="prose prose-lg max-w-none">
            
            {/* Welcome */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">Welcome to VapourVape Rewards</h2>
              <p className="text-vapour-700 mb-4">
                These Terms and Conditions ("Terms") govern your participation in the VapourVape Rewards loyalty program and use of our mobile application. By creating an account or using our services, you agree to these Terms.
              </p>
              <div className="bg-vapour-50 border border-vapour-200 rounded-lg p-4">
                <p className="text-vapour-800 font-medium">
                  <strong>Quick Summary:</strong> Our loyalty program lets you earn stamps with purchases and redeem them for rewards. These terms explain how it all works and what we expect from each other.
                </p>
              </div>
            </section>

            {/* Eligibility */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">1. Who Can Join</h2>
              
              <h3 className="text-xl font-medium text-vapour-800 mb-3">Age Requirements</h3>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li>You must be <strong>18 years or older</strong> to participate in our loyalty program</li>
                <li>Valid government-issued ID may be required for verification</li>
                <li>Minors cannot create accounts or participate in the program</li>
              </ul>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">Account Limits</h3>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li><strong>One account per person</strong> - multiple accounts are not allowed</li>
                <li>Accounts cannot be shared, transferred, or sold</li>
                <li>You must provide accurate personal information</li>
              </ul>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">Geographic Restrictions</h3>
              <p className="text-vapour-700">
                The VapourVape Rewards program is available to customers who shop at participating VapourVape locations. Program benefits may vary by location.
              </p>
            </section>

            {/* How It Works */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">2. How the Loyalty Program Works</h2>
              
              <h3 className="text-xl font-medium text-vapour-800 mb-3">Earning Stamps</h3>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <p className="text-green-800 font-medium">
                  <strong>Simple:</strong> Make a qualifying purchase → Get a stamp → Collect 6 stamps → Redeem for rewards!
                </p>
              </div>
              
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li><strong>1 stamp per qualifying purchase</strong> at VapourVape locations</li>
                <li>Staff will scan your QR code or enter your customer code to add stamps</li>
                <li>Bonus stamps may be awarded during special promotions</li>
                <li>Stamps are added to your account immediately upon purchase</li>
                <li>Minimum purchase amounts may apply (varies by location)</li>
              </ul>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">Redeeming Rewards</h3>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li><strong>6 stamps = 1 reward</strong> (typically a free product or discount)</li>
                <li>Rewards must be redeemed in-store at participating locations</li>
                <li>Show your app to staff to redeem rewards</li>
                <li>Rewards are subject to availability and may be substituted</li>
                <li>Rewards cannot be combined with other offers unless specified</li>
                <li>Rewards have no cash value and cannot be sold or transferred</li>
              </ul>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">Stamp Validity</h3>
              <ul className="list-disc pl-6 text-vapour-700 space-y-2">
                <li>Stamps <strong>do not expire</strong> as long as your account remains active</li>
                <li>Accounts are considered inactive after 12 months of no purchases</li>
                <li>We'll send you a reminder before any stamps expire due to inactivity</li>
                <li>Reactivate your account by making a purchase within the notice period</li>
              </ul>
            </section>

            {/* Account Responsibilities */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">3. Your Account Responsibilities</h2>
              
              <h3 className="text-xl font-medium text-vapour-800 mb-3">Account Security</h3>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li>Keep your login credentials secure and confidential</li>
                <li>Don't share your account with others</li>
                <li>Report any unauthorized use immediately</li>
                <li>You're responsible for all activity on your account</li>
              </ul>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">Accurate Information</h3>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li>Provide truthful and accurate personal information</li>
                <li>Update your information when it changes</li>
                <li>Ensure your email address is current for important notifications</li>
              </ul>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">Proper Use</h3>
              <ul className="list-disc pl-6 text-vapour-700 space-y-2">
                <li>Use the app only for its intended purpose</li>
                <li>Don't attempt to manipulate or exploit the system</li>
                <li>Be respectful to staff and other customers</li>
                <li>Follow all store policies and local laws</li>
              </ul>
            </section>

            {/* What's Not Allowed */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">4. Prohibited Activities</h2>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-red-800 font-medium">
                  <strong>Warning:</strong> These activities may result in account suspension or termination
                </p>
              </div>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">Fraud and Abuse</h3>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li>Creating multiple accounts to gain extra benefits</li>
                <li>Sharing or selling your account to others</li>
                <li>Attempting to earn stamps without making legitimate purchases</li>
                <li>Using fake or stolen payment methods</li>
                <li>Manipulating the QR code or customer code system</li>
              </ul>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">Technical Misuse</h3>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li>Attempting to hack, reverse engineer, or exploit the app</li>
                <li>Using automated tools or bots</li>
                <li>Interfering with the app's normal operation</li>
                <li>Accessing other users' accounts or data</li>
              </ul>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">Inappropriate Behavior</h3>
              <ul className="list-disc pl-6 text-vapour-700 space-y-2">
                <li>Harassing or abusing staff or other customers</li>
                <li>Using the service for illegal activities</li>
                <li>Violating store policies or local regulations</li>
                <li>Providing false information or impersonating others</li>
              </ul>
            </section>

            {/* Program Changes */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">5. Program Changes and Updates</h2>
              
              <h3 className="text-xl font-medium text-vapour-800 mb-3">Our Right to Modify</h3>
              <p className="text-vapour-700 mb-4">
                We may update the loyalty program from time to time to improve your experience or comply with legal requirements. Changes may include:
              </p>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li>Adjusting how stamps are earned or redeemed</li>
                <li>Changing reward options or values</li>
                <li>Updating app features and functionality</li>
                <li>Modifying program terms and conditions</li>
              </ul>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">How We'll Notify You</h3>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li><strong>Email notifications</strong> for significant changes</li>
                <li><strong>In-app messages</strong> when you open the app</li>
                <li><strong>Website updates</strong> with the latest terms</li>
                <li><strong>30 days advance notice</strong> for major changes when possible</li>
              </ul>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">Your Options</h3>
              <p className="text-vapour-700">
                If you don't agree with program changes, you can close your account before the changes take effect. Continued use of the program after changes means you accept the new terms.
              </p>
            </section>

            {/* Privacy and Data */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">6. Privacy and Your Data</h2>
              
              <h3 className="text-xl font-medium text-vapour-800 mb-3">What We Collect</h3>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li><strong>Account information:</strong> Name, email, phone number</li>
                <li><strong>Purchase history:</strong> Stamps earned, rewards redeemed</li>
                <li><strong>App usage:</strong> How you interact with our app</li>
                <li><strong>Device information:</strong> For security and technical support</li>
              </ul>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">How We Use It</h3>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li>Operate and improve the loyalty program</li>
                <li>Send you program updates and promotional offers</li>
                <li>Provide customer support</li>
                <li>Prevent fraud and ensure security</li>
                <li>Comply with legal requirements</li>
              </ul>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-800">
                  <strong>Full Details:</strong> Read our complete <button onClick={() => navigate('/privacy')} className="underline hover:text-blue-900">Privacy Policy</button> for comprehensive information about how we handle your data.
                </p>
              </div>
            </section>

            {/* Limitations and Disclaimers */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">7. Important Limitations</h2>
              
              <h3 className="text-xl font-medium text-vapour-800 mb-3">Service Availability</h3>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li>The app may be temporarily unavailable for maintenance or updates</li>
                <li>Internet connectivity is required for most features</li>
                <li>We're not responsible for issues caused by your device or internet connection</li>
                <li>Store participation may vary by location</li>
              </ul>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">Reward Availability</h3>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li>Rewards are subject to availability and may be substituted</li>
                <li>Some rewards may have expiration dates or usage restrictions</li>
                <li>We reserve the right to modify or discontinue rewards</li>
                <li>Rewards cannot be exchanged for cash</li>
              </ul>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">Technical Issues</h3>
              <p className="text-vapour-700">
                While we strive for reliability, technical issues may occasionally occur. If you experience problems earning or redeeming stamps, contact our support team for assistance.
              </p>
            </section>

            {/* Account Termination */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">8. Account Closure</h2>
              
              <h3 className="text-xl font-medium text-vapour-800 mb-3">Closing Your Account</h3>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li>You can close your account at any time through the app or by contacting support</li>
                <li>Unused stamps will be forfeited when you close your account</li>
                <li>We recommend redeeming any available rewards before closing</li>
                <li>Account closure is permanent and cannot be undone</li>
              </ul>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">When We May Close Accounts</h3>
              <p className="text-vapour-700 mb-4">
                We may suspend or close accounts that:
              </p>
              <ul className="list-disc pl-6 text-vapour-700 mb-4 space-y-2">
                <li>Violate these terms and conditions</li>
                <li>Engage in fraudulent or abusive behavior</li>
                <li>Remain inactive for extended periods</li>
                <li>Pose security risks to other users</li>
              </ul>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">Appeal Process</h3>
              <p className="text-vapour-700">
                If your account is suspended or closed, you can contact our support team to discuss the situation. We'll review each case fairly and may reinstate accounts when appropriate.
              </p>
            </section>

            {/* Legal Stuff */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">9. Legal Information</h2>
              
              <h3 className="text-xl font-medium text-vapour-800 mb-3">Liability Limits</h3>
              <p className="text-vapour-700 mb-4">
                Our liability for any issues with the loyalty program is limited to the value of unused stamps in your account. We're not responsible for indirect damages or losses.
              </p>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">Governing Law</h3>
              <p className="text-vapour-700 mb-4">
                These terms are governed by the laws of [Your Jurisdiction]. Any disputes will be resolved in the courts of [Your Jurisdiction].
              </p>

              <h3 className="text-xl font-medium text-vapour-800 mb-3">Entire Agreement</h3>
              <p className="text-vapour-700">
                These Terms and Conditions, along with our Privacy Policy, represent the complete agreement between you and VapourVape regarding the loyalty program.
              </p>
            </section>

            {/* Getting Help */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-vapour-900 mb-4">10. Questions and Support</h2>
              
              <div className="bg-vapour-50 border border-vapour-200 rounded-lg p-6">
                <h3 className="text-lg font-medium text-vapour-900 mb-3">Need Help?</h3>
                <div className="space-y-3 text-vapour-700">
                  <div>
                    <p><strong>Email Support:</strong> support@vapourvape.com</p>
                    <p className="text-sm text-vapour-600">We typically respond within 24 hours</p>
                  </div>
                  
                  <div>
                    <p><strong>Phone Support:</strong> +1234567890</p>
                    <p className="text-sm text-vapour-600">Monday - Friday, 9:00 AM - 6:00 PM</p>
                  </div>
                  
                  <div>
                    <p><strong>In-Store Help:</strong> Ask any VapourVape staff member</p>
                    <p className="text-sm text-vapour-600">They can help with account issues and rewards</p>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-vapour-200">
                  <p className="text-sm text-vapour-600">
                    <strong>Common Issues:</strong> Can't earn stamps? App not working? Rewards not showing? We're here to help!
                  </p>
                </div>
              </div>
            </section>

          </div>

          {/* Footer */}
          <div className="mt-12 pt-8 border-t border-dark-200">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="text-sm text-vapour-600">
                <p>Last updated: September 4, 2025</p>
                <p>Version 1.0</p>
              </div>
              <div className="flex gap-4">
                <button
                  onClick={() => navigate('/privacy')}
                  className="text-vapour-600 hover:text-vapour-900 text-sm underline"
                >
                  Privacy Policy
                </button>
                <button
                  onClick={() => navigate('/terms')}
                  className="text-vapour-600 hover:text-vapour-900 text-sm underline"
                >
                  Terms of Service
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="text-vapour-600 hover:text-vapour-900 text-sm underline"
                >
                  Back to Home
                </button>
              </div>
            </div>
          </div>

        </div>
      </main>
    </div>
  )
}